/**
 * The class strings the overlay surfaces share (§ Overlays), taken from shadcn/ui's Dialog,
 * Sheet and Popover and themed through the variables of `packages/tokens` (2026-09-15, by the
 * owner's decision). They live apart from the components because Dialog composes AlertDialog
 * for its unsaved-changes confirmation, Drawer and SidePeek share the sheet's parts, and the
 * pickers, Select and the editor's suggestion menu float on the Popover's surface until slice (f)
 * takes them.
 *
 * What differs from the sources, and why: the scrim is `--color-scrim` where the sources write
 * black at 50 percent or the background at 80 percent with a backdrop blur, because the scrim is
 * a semantic token with a value per theme and the theme defines no blur; and every enter and exit
 * runs on the duration tokens (the slow tier for a panel, the base tier for its exit and for a
 * popover), so `data-motion="reduced"` resolves them to zero (§ Motion).
 */
export const scrimClassName =
  "fixed inset-0 z-(--layer-overlay) bg-scrim duration-(--duration-base) data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0";

/** The Popover's floating surface without its padding or origin, which each Radix primitive names for itself. */
export const popoverSurfaceClassName =
  "z-(--layer-overlay) rounded-md border bg-popover text-popover-foreground shadow-md outline-hidden duration-(--duration-base) data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95";

/**
 * A dialog's title and description, from shadcn/ui's DialogTitle and DialogDescription. The title
 * takes the `--text-h2` step whole, its line height included, and balances its lines, so a title
 * that wraps reads as a heading rather than two lines set solid (§ Type).
 */
export const dialogTitleClassName = "m-0 text-h2 text-balance";
export const dialogDescriptionClassName = "text-sm text-muted-foreground";

/** The side sheet's panel, header, title and scrolling body, from shadcn/ui's Sheet; Drawer and SidePeek both draw them. */
export const sheetPanelClassName = "flex flex-col bg-background shadow-lg";
export const sheetHeaderClassName = "flex items-center gap-3 p-4";
export const sheetTitleClassName = "min-w-0 flex-1 truncate font-semibold text-foreground";
export const sheetBodyClassName = "min-h-0 flex-1 overflow-y-auto px-4 pb-4";
